import React, { useState, useContext, useEffect, Fragment } from 'react';
import { AuthContext } from '../contexts/auth';
import { Dialog, Transition } from '@headlessui/react'
import { PencilSquareIcon, TrashIcon, BriefcaseIcon, InformationCircleIcon, CheckIcon } from '@heroicons/react/24/outline';
import { deleteTimeSlotCompany, deleteWeeklyWorkingHoursSlot, updateWeeklyWorkingHoursCompany, deleteWeeklyWorkingHours } from '../services/api_working_hours';
import AddNewWeekTimeSlot from './add_new_week_time_slot';
import Swal from 'sweetalert2';
import EditTimeSlot from './edit_time_slot';
import { formatTime } from '../utils/timeslots';
import ComboboxAutocomplete from './combobox_autocomplete';
import { listOrganizationalUnits } from '../services/api_org_unit';
import WeekCalendar from './week_calendar';
import WeekCalendarEditShift from './week_calendar_edit_shift';
import Fetching from './fetching';
import Empty from './empty';
import CustomTooltipComponent from './custom_tooltip_component';



const ShiftTableWeeklyShow = ({ weeklyWorkingHours, loading, onDeleteWeekShift, onCreateWeekWorkingHour, onDeleteWeekDay, onUpdateWeekWorkingHours, onUpdatedTimeSlot, onUnitChange, onDeleteWeekTimeSlot  }) => {
  const {token } = useContext(AuthContext);
  const daysOfWeek = ['', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica']; // il primo è vuoto perche chiara conta da 1
  // const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [weekWorkingHoursData, setWeekWorkingHoursData] = useState(weeklyWorkingHours);
  const [breaks, setBreaks] = useState(null);
  const [addTimeSlot, setAddTimeSlot] = useState(false);
  const [editTimeSlot, setEditTimeSlot] = useState(null);
  const [editTimeSlotIndex, setEditTimeSlotIndex] = useState(null);
  const [weekWorkingHourId, setWeekWorkingHourId] = useState(null); 
  const [weekWorkingHourObject, setWeekWorkingHourObject] = useState(null); 
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditTimeSlotDialog, setOpenEditTimeSlotDialog] = useState(false);
  const [openCreateTimeSlotDialog, setOpenCreateTimeSlotDialog] = useState(false);
  const [isEditingName, setIsEditingName] = useState({});
  const [newNameWwh, setNewNameWwh] = useState('');
  const [editWeekDay, setEditWeekDay] = useState(null);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingShiftId, setEditingShiftId] = useState(null); 

  useEffect(() => {
    async function fetchData() {
      try {
        const unitsData = await listOrganizationalUnits(token, true);
        setUnits(unitsData.data);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching units:', error);
        setError(true);
        setIsLoading(false);
      }
    }
    fetchData();
  }, [token]);

  const handleSelectUnit = (selected) => {
    const unit = selected === 'all' || !selected ? null : selected;
    setSelectedUnit(unit);
    onUnitChange(unit, 'SETTIMANALE');
  };

  const handleResetSelection = () => {
    setSelectedUnit(null);
    onUnitChange(null, 'SETTIMANALE');
  };

  const handleDeleteWeekShift = async (id) => {
    Swal.fire({
      icon: "warning",
      text: `Il turno settimanale verrà eliminato anche dal calendario dei dipendenti ai quali è stato assegnato. Sei sicuro di voler procedere?`,
      showCancelButton: true,
      confirmButtonText: "Sì",
      cancelButtonText: "Annulla",
      cancelButtonColor: "#d1d5db",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteWeeklyWorkingHours(token, id);
          const shiftDelete = id;
          const updatedWorkingHours = weeklyWorkingHours.filter(shift => shift.id !== id); 
          setWeekWorkingHoursData(updatedWorkingHours);
          onDeleteWeekShift(id);
          Swal.fire({
            position: "center",
            icon: "success",
            title: "Turno eliminato con successo!",
            showConfirmButton: false,
            timer: 1500
          });
        } catch (error) {
          console.error('Errore durante l\'eliminazione del turno', error);
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Errore durante l'eliminazione del turno",
          });
        }
      }
    });
  };

  // const handleEditShift = (index: number) => {
  //   setEditingIndex(index);
  // };

  const calculateDuration = (start, end) => {
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    const startDate = new Date(0, 0, 0, startH, startM);
    const endDate = new Date(0, 0, 0, endH, endM);

    if (endDate <= startDate) endDate.setDate(endDate.getDate() + 1);
    return Math.floor((endDate - startDate) / 60000);
  };

  const calculateTotalBreakDuration = (pauseList) => {
    return pauseList.reduce((acc, pause) => {
      const duration = calculateDuration(pause.start_at, pause.end_at);
      return acc + duration;
    }, 0);
  };

  const getNetDuration = (updatedTimeSlot) => {
    const slotDuration = calculateDuration(updatedTimeSlot.start_at, updatedTimeSlot.end_at);
    const breakDuration = calculateTotalBreakDuration(updatedTimeSlot.breaks || []);
    return slotDuration - breakDuration;
  };

  const handleUpdatedTimeSlot = (updatedTimeSlot) => {
    const updatedDuration = getNetDuration(updatedTimeSlot);

    const newUpdatedTimeSlot = { ...updatedTimeSlot, duration: updatedDuration };

    const updatedWorkingHoursData = weekWorkingHoursData.map((weekWorkingHour) => {
      if (weekWorkingHour.slots_by_weekday) {
        const updatedSlotsByWeekday = weekWorkingHour.slots_by_weekday.map((slotByWeekday) => {
          const updatedTimeSlots = slotByWeekday.time_slots.map((timeSlot) =>
            timeSlot.id === newUpdatedTimeSlot.id ? newUpdatedTimeSlot : timeSlot
          );
          return { ...slotByWeekday, time_slots: updatedTimeSlots };
        });
        return { ...weekWorkingHour, slots_by_weekday: updatedSlotsByWeekday };
      }
      return weekWorkingHour;
    });

    setWeekWorkingHoursData(updatedWorkingHoursData);
    onUpdatedTimeSlot(newUpdatedTimeSlot);
  };

  const handleEditWeekTimeSlot = (selectedTimeSlot, index, weekWorkingHourId, week_day) => {
    setError('');
    if (addTimeSlot) {
      setError("Per procedere con la modifica della fascia oraria è necessario chiudere la creazione della nuova fascia oraria");
      return;
    }
    setEditTimeSlot([selectedTimeSlot]);
    setEditTimeSlotIndex(index);
    setWeekWorkingHourId(weekWorkingHourId);
    setOpenEditTimeSlotDialog(true);
    setEditWeekDay(week_day);
    onUpdatedTimeSlot(selectedTimeSlot);
  };

  const handleCreateWeekTimeSlot = (newWeekWorkingHour, weekWorkingHourId) => {
    setWeekWorkingHoursData((prevWorkingHoursData) => {
      const data =  prevWorkingHoursData.map((weekWorkingHour) => {
        if (weekWorkingHour.id === weekWorkingHourId) {
          //const updatedTimeSlots = [...weekWorkingHour.time_slots, newWeekWorkingHour];
          return {
            ...weekWorkingHour,
            slots_by_weekday: [
              ...(weekWorkingHour.slots_by_weekday || []).filter(wwhs => wwhs.week_day !== newWeekWorkingHour.week_day),
              newWeekWorkingHour
            ]
          };
        }
        return weekWorkingHour;
      });
      // const updatedWeekWorkingHour = data.find(wwh => wwh.id === weekWorkingHourId);
      onCreateWeekWorkingHour(newWeekWorkingHour, weekWorkingHourId);
      return data;
    });
  };
  
  const handleDeleteWeekTimeSlot = async (id, wwhs, wwhid) => {
    try {
      const isLastTimeSlot = (wwhs.time_slots || []).length === 1;

      if (isLastTimeSlot) {
        await handleDeleteWeeklyWorkingHourSlot(wwhs.id);
      } else {
        await deleteTimeSlotCompany(token, id);
        const updatedWorkingHours = weekWorkingHoursData.map(wwh => {
          if(wwh.id !== wwhid)
            return wwh;

          wwhs.time_slots = wwhs.time_slots.filter(ts => ts.id !== id);
          return {
              ...wwh,
              slots_by_weekday: [
                ...(wwh.slots_by_weekday || []).filter(sbw => sbw.id !== wwhs.id),
                wwhs
              ]
          };
        });
        setWeekWorkingHoursData(updatedWorkingHours);
        onDeleteWeekTimeSlot(id);
        }
    } catch (error) {
      console.error('Errore durante l\'eliminazione della fascia oraria', error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Errore durante l\'eliminazione della fascia oraria"
      });
    }
  };


  const handleDeleteWeeklyWorkingHourSlot = async (id) => {
    try {
      await deleteWeeklyWorkingHoursSlot(token, id);
  
      const updatedWorkingHours = weekWorkingHoursData.map(wh => {
        const updatedSlotsByWeekday = wh.slots_by_weekday?.filter(wwhs => wwhs.id !== id) || [];
        return { ...wh, slots_by_weekday: updatedSlotsByWeekday };
      });
      setWeekWorkingHoursData(updatedWorkingHours);
      onDeleteWeekDay(id);
  
      Swal.fire({
        position: "center",
        icon: "success",
        title: "Turno eliminato con successo!",
        showConfirmButton: false,
        timer: 1500
      });
    } catch (error) {
      console.error('Errore durante l\'eliminazione del turno', error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Errore durante l'eliminazione del turno",
      });
    }
  }; 

  const handleAddTimeSlotWeekHour = (dayIndex, slotData, weekWorkingHourId, weekWorkingHour) => {  

    setError('');
    if (editTimeSlot !== null) {
      setError("Concludi prima l'attuale operazione di modifica.");
      return;
    }

    setSelectedDayIndex(dayIndex);
    setWeekWorkingHourId(weekWorkingHourId);
    setWeekWorkingHourObject(weekWorkingHour);
    setOpenCreateTimeSlotDialog(true);
    setAddTimeSlot(true);
  };

  const handleCancelAddWeekTimeSlot = () => {
    setWeekWorkingHourId(null);
    setAddTimeSlot(false);
  };

  const handleEditNameClick = (index, name) => {
    setNewNameWwh(name); 
    setIsEditingName({ ...isEditingName, [index]: true }); 
  };


  const handleSaveNameClick = async (index, WWHid) => {
    try {
      setIsSaving(true);
      
      const updatedWorkingHour = await updateWeeklyWorkingHoursCompany(token, WWHid, newNameWwh);
      
      onUpdateWeekWorkingHours(WWHid, updatedWorkingHour.name);
  
      setIsEditingName({ ...isEditingName, [index]: false });
  
      Swal.fire({
        position: "center",
        icon: "success",
        title: "Salvato!",
        showConfirmButton: false,
        timer: 1500
      });
    } catch (error) {
      console.error("Errore durante il salvataggio del nome del turno:", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Errore durante l'aggiornamento",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelNameClick = (index) => {
    setIsEditingName({ ...isEditingName, [index]: false }); 
  };

  // const formatMinutesToHours = (minutes: number) => {
  //   const totalHours = Math.floor(minutes / 60);
  //   const remainingMinutes = minutes % 60;

  //   if (totalHours === 0 && remainingMinutes === 0) {
  //       return '0 min';
  //   } else if (totalHours === 0) {
  //       return `${remainingMinutes} min`;
  //   } else if (remainingMinutes === 0) {
  //       return `${totalHours} h`;
  //   } else {
  //       return `${totalHours} h, ${remainingMinutes} min`;
  //   }
  // };

  const convertToShiftsByDay = (slotsByWeekday) => {
    const result = {};

    (slotsByWeekday || []).forEach((slot) => {
      const dayName = daysOfWeek[slot.week_day]; // es: "Lunedì"
      const timeSlots = slot.working_hour?.time_slots || slot.time_slots || [];

      result[dayName] = (result[dayName] || []).concat(
        timeSlots.map((ts) => ({
          workStartTime: formatTime(ts.start_at),
          workEndTime: formatTime(ts.end_at),
          breaks: ts.breaks?.map((br) => ({
            start_at: formatTime(br.start_at),
            end_at: formatTime(br.end_at),
          })) || [],
        }))
      );
    });

    return result;
  };

  const shiftsByDay = convertToShiftsByDay(weeklyWorkingHours.slots_by_weekday);
  const getSlotsByDayArray = (slots_by_weekday) => {
    const slotsByDay = Array(7).fill(null);
    slots_by_weekday?.forEach((s) => {
      slotsByDay[s.week_day] = s;
    });
    return slotsByDay;
  };

  return (
    <div className="m-5 border border-solid-2 border-gray-200 rounded-md shadow-md" style={{ minHeight: '100vh', maxHeight: '100vh', overflowY: 'auto' }}>
      <div className='grid grid-cols-3 bg-gray-50 text-sm  font-bold text-gray-500 text-center py-3 uppercase sticky top-0 z-10 items-center'>
        <div className='col-span-1 flex justify-start pl-4'>
          <button onClick={() => setOpenDialog(true)}>
            <InformationCircleIcon className='w-7 inline-block'/> 
          </button>
        </div>
        <div className='col-span-1 justify-end underline'>
           Turni Settimanali
        </div>
        <div className='col-span-3 mt-2'>
          {selectedUnit?.id ? (
            <>
              Stai visualizzando i turni dell'Unità{" "}
              <span className="text-indigo-600 font-semibold">{selectedUnit.name}</span>
            </>
          ) : (
            <>
              Stai visualizzando i turni settimanali{" "}
              <span className="text-indigo-600 font-semibold">GENERICI</span>
            </>
          )}
        </div>
      </div>

      <div className='bg-gray-50 text-sm flex text-gray-500 text-center pt-3 pb-6 uppercase sticky top-[80px] z-10 items-center justify-between px-4'>
        <div className='px-5 font-bold flex-shrink-0'>
          Visualizza
        </div>
        <div className='me-5 w-1/2 z-10'>
          <ComboboxAutocomplete
            options={units}
            bgActiveColor="bg-indigo-50"
            textActiveColor="text-gray-900"
            placeholder="Seleziona l'Unità Organizzativa per visualizzare i turni collegati alla specifica Unità"
            onChange={handleSelectUnit}
            value={selectedUnit}
            positionOptions='absolute'
          />
        </div>
        <button
          className={`inline-flex items-center rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${!selectedUnit ? 'invisible' : ''}`}
          onClick={handleResetSelection}
          disabled={!selectedUnit}
        >
          Reset selezione
        </button>
      </div>

      {loading && (
        <Fetching />
      )}
      {!loading && (
        <>
          {weeklyWorkingHours && weeklyWorkingHours.length > 0 ? (
            <div className="space-y-6 mt-4 px-8">
              {weeklyWorkingHours
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((weekWorkingHour, index) => {
                  const slotsByDay = Array(7).fill(null);
                  weekWorkingHour.slots_by_weekday?.forEach((slot) => {
                    slotsByDay[slot.week_day] = slot;
                  });

                  return (
                    <div key={index} className="border-b border-gray-200 pb-4">
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center">
                          {isEditingName[index] ? (
                            <>
                              <input
                                type="text"
                                value={newNameWwh}
                                onChange={(e) => setNewNameWwh(e.target.value)}
                                className="text-sm font-medium text-gray-700 py-2 px-3 rounded border border-gray-300 focus:outline-none focus:border-indigo-500"
                              />
                              <button onClick={() => handleSaveNameClick(index, weekWorkingHour.id)} className="text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-500 py-1.5 px-4 rounded mx-2">
                                SALVA
                              </button>
                              <button onClick={() => handleCancelNameClick(index)} className="text-xs font-semibold text-gray-700 bg-gray-200 py-1.5 px-4 rounded">
                                ANNULLA
                              </button>
                            </> 
                          ) : (
                            <>
                              <span className='uppercase text-sm font-semibold text-gray-600'>{weekWorkingHour.name}</span>
                              {editingShiftId === weekWorkingHour.id && (
                                <CustomTooltipComponent title='Modifica nome' placement="bottom" arrow>
                                  <button onClick={() => handleEditNameClick(index, weekWorkingHour.name)} className="text-sm font-medium text-gray-700 ml-4">
                                    <PencilSquareIcon className='w-5 h-5' />
                                  </button>
                                </CustomTooltipComponent>
                              )}
                            </>
                          )}
                        </div>


                        <div className="flex sm:mt-0 justify-end gap-2">
                          {editingShiftId === weekWorkingHour.id ? (
                            <button
                              onClick={() => setEditingShiftId(null)}
                              className="bg-gray-400 text-white text-sm px-2 py-1 font-semibold rounded-md hover:bg-gray-300 flex items-center"
                            >
                              <PencilSquareIcon className="h-4 w-4 me-1" />
                              CHIUDI MODIFICA
                            </button>
                          ) : (
                            <button
                              onClick={() => setEditingShiftId(weekWorkingHour.id)}
                              className="bg-indigo-600 text-white text-sm px-2 py-1 font-semibold rounded-md hover:bg-indigo-500 flex items-center"
                            >
                              <PencilSquareIcon className="h-4 w-4 me-1" />
                              MODIFICA
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteWeekShift(weekWorkingHour.id)}
                            className="bg-indigo-600 text-white text-sm px-2 py-1 font-semibold rounded-md hover:bg-indigo-500 align-center justify-center flex"
                          >
                            <TrashIcon className='h-4 w-4 inline-block align-center mt-0.5 me-1' />
                            ELIMINA
                          </button>
                        </div>                    
                      </div>
                      {weekWorkingHour?.ou && (
                        <div className="justify-center text-left items-center">
                          <span className='text-xs font-bold text-gray-500 rounded-md bg-indigo-50 p-1'>Unità associata:  {weekWorkingHour.ou_name}</span>
                        </div>
                      )}
                      <WeekCalendarEditShift 
                        daysOfWeek={['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica']}
                        slotsByDay={slotsByDay}
                        weekWorkingHourId={weekWorkingHour.id}
                        handleAddTimeSlot={(dayIndex, slotData) => handleAddTimeSlotWeekHour(dayIndex + 1, slotData, weekWorkingHour.id, weekWorkingHour)}
                        onDeleteWeeklyWorkingHourSlot={handleDeleteWeeklyWorkingHourSlot}
                        onDeleteTimeSlot={handleDeleteWeekTimeSlot}
                        onEditWeekTimeSlot={handleEditWeekTimeSlot}
                        editingShiftId={editingShiftId}
                      />
                    </div>
                  );
                })}
            </div>
          ) : (
            <Empty />
          )}
        </>
      )}

      {(addTimeSlot && weekWorkingHourId && openCreateTimeSlotDialog) && (
        <Transition.Root show={openCreateTimeSlotDialog} as={Fragment}>
          <Dialog className="relative z-10" onClose={setOpenCreateTimeSlotDialog}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto w-[100%]">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-gray-50 items-center flex justify-center mx-8 shadow-xl transition-all my-8 w-3/4">
                    <div className='w-full'>
                      <div className='px-4 bg-gray-50'>
                        {error && (
                          <div className="grid grid-cols-1 mx-4 mt-3">
                            <p className="text-red-500 text-sm text-center">{error}</p>
                          </div>
                        )}
                        <AddNewWeekTimeSlot 
                          weekWorkingHour={weekWorkingHourId}
                          selectedOuId={selectedUnit?.id}
                          onCancelEdit={handleCancelAddWeekTimeSlot}
                          onWeekWorkingHour={handleCreateWeekTimeSlot}
                          selectedDayNumber={selectedDayIndex}
                          weekWorkingHourObject={weekWorkingHourObject}
                          onDeleteWeekTimeSlot={handleDeleteWeekTimeSlot}
                        />
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      )}

      {(editTimeSlot !== null && openEditTimeSlotDialog) && (
        <div>
          <Transition.Root show={openEditTimeSlotDialog} as={Fragment}>
            <Dialog className="relative z-10" onClose={setOpenEditTimeSlotDialog}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
              </Transition.Child>

              <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  >
                    <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-gray-50 items-center flex justify-center mx-8 shadow-xl transition-all my-8 w-[60%]">
                      <div className='w-full px-2'>
                        <div className='col-span-3 px-4 bg-gray-50'>
                          {error && (
                            <div className="grid grid-cols-1 mx-4">
                              <p className="text-red-500 text-sm text-center">{error}</p>
                            </div>
                          )}
                          <EditTimeSlot
                            timeSlotObject={editTimeSlot[0]}
                            onClose={() => setEditTimeSlot(null)}
                            onShiftPreview={() => setEditTimeSlot(null)}
                            onCancelEdit={() => setEditTimeSlot(null)}
                            onUpdatedTimeSlot={(updatedTimeSlot) => handleUpdatedTimeSlot(updatedTimeSlot)}
                            timeSlots={editWeekDay?.working_hour?.time_slots || editWeekDay?.time_slots || []}
                          />
                        </div>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition.Root>
        </div>
      )}
         
      {/* {weeklyWorkingHours && weeklyWorkingHours.length > 0 ? (
        <div className="space-y-6 mt-4 px-8">
          {weeklyWorkingHours
            .sort((a, b) => b.id - a.id)
            .map((weekWorkingHour, index) => {
              const slotsByDay = Array(7).fill(null);
              weekWorkingHour.slots_by_weekday?.forEach((slot) => {
                slotsByDay[slot.week_day] = slot;
              });

              return (
                <div key={index} className="border-b border-gray-200 pb-4">
                  <div className="flex items-center justify-between mb-2 w-full">
                    <div className="flex items-center">
                      {isEditingName[index] ? (
                        <>
                          <input
                            type="text"
                            value={newNameWwh}
                            onChange={(e) => setNewNameWwh(e.target.value)}
                            className="text-sm font-medium text-gray-700 py-2 px-3 rounded border border-gray-300 focus:outline-none focus:border-indigo-500"
                          />
                          <button onClick={() => handleSaveNameClick(index, weekWorkingHour.id)} className="text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 py-1 px-4 rounded mx-2">
                            Salva
                          </button>
                          <button onClick={() => handleCancelNameClick(index)} className="text-sm font-medium text-gray-700 bg-gray-200 py-1 px-4 rounded">
                            Annulla
                          </button>
                        </> 
                      ) : (
                        <>
                          <span className='uppercase text-sm font-semibold text-gray-600'>{weekWorkingHour.name}</span>
                          <button onClick={() => handleEditNameClick(index, weekWorkingHour.name)} className="text-sm font-medium text-gray-700 ml-4">
                            <PencilSquareIcon className='w-5 h-5' />
                          </button>
                        </>
                      )}
                    </div>
                    <button
                      className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm px-2 py-1 font-semibold rounded-md"
                      title="Elimina"
                      onClick={() => handleDeleteWeekShift(weekWorkingHour.id)}
                    >
                      ELIMINA TURNO
                    </button>
                  </div>
                  {weekWorkingHour?.ou && (
                    <div className="justify-center text-left items-center">
                      <span className='text-xs font-bold text-gray-500 rounded-md bg-indigo-50 p-1'>Unità associata:  {weekWorkingHour.ou_name}</span>
                    </div>
                  )}

                  <WeekCalendarEditShift 
                    daysOfWeek={['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']}
                    slotsByDay={slotsByDay}
                    handleAddTimeSlot={(dayIndex, slotData) => handleAddTimeSlotWeekHour(dayIndex + 1, slotData, weekWorkingHour.id)}
                    onDeleteWeeklyWorkingHourSlot={handleDeleteWeeklyWorkingHourSlot}
                    onDeleteTimeSlot={handleDeleteWeekTimeSlot}
                  />
                </div>
              );
            })}
        </div>
      ) : null} */}

   

      {openDialog && (
        <Transition.Root show={openDialog} as={Fragment}>
          <Dialog style={{ position: 'relative', zIndex: '9999' }} onClose={setOpenDialog}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>
    
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                    <div>
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100">
                        <InformationCircleIcon className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div className="mt-3 text-center sm:mt-5">
                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          Gestione turni settimanali
                        </Dialog.Title>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            È possibile associare un turno giornaliero a ciascun giorno che compone il turno settimanale. Le modifiche al turno giornaliero devono essere effettuate nella sezione 'Turni giornalieri' e non all'interno del turno settimanale stesso. Le modifiche apportate al turno giornaliero influenzeranno anche i turni settimanali che lo includono.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-6 justify-center flex">
                      <button
                        type="button"
                        className="py-2 bg-gray-200 rounded-md mb-2 w-28 focus:outline-none text-gray-700 hover:bg-gray-300"
                        onClick={() => setOpenDialog(false)}
                      >
                        Chiudi
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      )}
    </div>
  );

}

export default ShiftTableWeeklyShow;
